<script setup lang="ts">
    import { ModelType } from '~/app/base/schemas/BaseSchema';
    import EndpointFactory from '~/app/factories/EndpointFactory';
    import type Worksite from '~/app/worksite/models/Worksite';
    import { useEntityPreview } from '~/composables/useEntityPreview';

    const props = defineProps<{
        id: string;
    }>();

    defineEmits<{
        close: [];
    }>();

    const typeRef = computed(() => 'worksite');
    const idRef = computed(() => props.id);
    const { t } = useTranslation();
    const { loading, error, fetchEntity, setRefreshCallback } = useEntityPreview(typeRef, idRef);

    const worksiteData = ref<Worksite | null>(null);

    const entityTitle = computed(() => worksiteData.value?.title ?? 'Chantier');

    const worksiteInfoFields = computed(() => {
        if (!worksiteData.value) return [];

        const worksite = worksiteData.value;
        const fields: Array<{ label: string; value: string }> = [];

        if (worksite.address?.display) fields.push({ label: t('form.labels.address'), value: worksite.address.display });
        if (worksite.contact?.displayName) fields.push({ label: t('form.labels.contact'), value: worksite.contact.displayName });
        if (worksite.startsAt) fields.push({ label: t('form.labels.startDate'), value: worksite.startsAt.toShortDayDateString() });
        if (worksite.endsAt) fields.push({ label: t('form.labels.endDate'), value: worksite.endsAt.toShortDayDateString() });

        return fields;
    });

    const fetchData = async () => {
        const endpoint = EndpointFactory.make(ModelType.WORKSITES);
        const response = await endpoint.setInclude('contact').retrieve(props.id);
        worksiteData.value = response.data as Worksite;
    };

    setRefreshCallback(fetchData);

    watch(
        () => props.id,
        () => fetchEntity(fetchData),
        { immediate: true },
    );
</script>

<template>
    <div class="flex flex-col h-full">
        <!-- Header -->
        <div class="flex items-center gap-[10px] px-[14px] py-[10px] border-b border-[#E7E7E7] min-h-[50px]">
            <button
                type="button"
                class="w-7 h-7 inline-flex items-center justify-center rounded-md text-[#42484E] hover:bg-[#F3F3F3] transition-colors cursor-pointer"
                @click="$emit('close')"
            >
                <i class="fa-solid fa-arrow-left text-[14px]" />
            </button>
            <span class="font-bricolage font-bold text-[14px] text-[#0F1020] truncate flex-1 leading-none">
                {{ entityTitle }}
            </span>
            <AiPageEntityPreviewActions :type="'worksite'" :id="id" />
        </div>

        <div v-if="loading" class="flex-1 flex items-center justify-center">
            <i class="fa-solid fa-spinner fa-spin text-gray-400" />
        </div>
        <div v-else-if="error" class="flex-1 flex items-center justify-center">
            <p class="text-[13px] text-gray-400">{{ $t('ai.preview.unavailable') }}</p>
        </div>
        <div v-else-if="worksiteData" class="flex-1 overflow-y-auto nice-scrollbar bg-gray-50">
            <div class="m-5 p-7 rounded-[8px] border border-[#E7E7E7] bg-white shadow-[0_1px_10px_rgba(0,0,0,0.04)]">
                <div class="flex items-center gap-[14px] mb-6">
                    <div class="flex items-center justify-center w-[52px] h-[52px] rounded-full bg-branding-brand-yellow text-black shrink-0 border border-[#E8B102]">
                        <i class="fa-regular fa-hard-hat text-[20px]" />
                    </div>
                    <div class="flex-1 min-w-0">
                        <p class="font-bricolage font-bold text-[20px] text-gray-900 truncate">
                            {{ worksiteData.title ?? $t('ai.preview.no_title') }}
                        </p>
                    </div>
                </div>

                <dl class="grid grid-cols-[140px_1fr] gap-y-[14px] font-inter text-[13px]">
                    <template
                        v-for="(field, index) in worksiteInfoFields"
                        :key="index"
                    >
                        <dt class="text-[11px] font-medium text-gray-400 uppercase tracking-wide">
                            {{ field.label }}
                        </dt>
                        <dd class="m-0 text-gray-900">
                            {{ field.value }}
                        </dd>
                    </template>
                </dl>

                <div v-if="worksiteData.notes" class="mt-6 pt-6 border-t border-gray-100">
                    <p class="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-2">
                        {{ $t('form.labels.notes') }}
                    </p>
                    <p class="text-[13px] text-gray-700 whitespace-pre-wrap leading-relaxed">
                        {{ worksiteData.notes }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>
